import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/platform/platformUi";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "—";
    }
}

const ACTION_LABELS = {
    "tenant.create": "Salon created",
    "tenant.update": "Salon updated",
    "plan.create": "Plan created",
    "plan.update": "Plan updated",
    "plan.delete": "Plan deleted",
    "plan.deactivate": "Plan deactivated",
    "billing.record": "Payment recorded",
    "user.update": "User updated",
    "settings.update": "Settings updated",
    "blog.create": "Blog post created",
    "blog.update": "Blog post updated",
    "blog.delete": "Blog post deleted",
};

export default function PlatformActivity() {
    const [rows, setRows] = useState([]);

    useEffect(() => {
        api.get("/platform/activity", { params: { limit: 200 } })
            .then((r) => setRows(r.data || []))
            .catch(() => toast.error("Could not load activity log"));
    }, []);

    return (
        <div className="space-y-6 p-6 lg:p-8" data-testid="platform-activity-page">
            <PageHeader
                eyebrow="System"
                title="Activity log"
                subtitle="Audit trail of platform admin actions — who did what and when"
            />

            <div className="overflow-x-auto rounded-xl border border-[#E8E4DE] bg-white">
                <table className="min-w-max w-full border-collapse">
                    <thead>
                        <tr className="border-b border-[#E8E4DE] text-left text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#8A8178]">
                            <th className="px-4 py-3">When</th>
                            <th className="px-4 py-3">Admin</th>
                            <th className="px-4 py-3">Action</th>
                            <th className="px-4 py-3">Summary</th>
                            <th className="px-4 py-3">Target</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className="border-b border-[#E8E4DE]/50 last:border-b-0">
                                <td className="px-4 py-3 text-sm text-[#8A8178] whitespace-nowrap">{formatDate(r.created_at)}</td>
                                <td className="px-4 py-3 text-sm font-medium text-[#3D2817]">{r.actor_email || "—"}</td>
                                <td className="px-4 py-3">
                                    <span className="rounded-full bg-[#FAF9F6] px-2.5 py-0.5 text-xs font-semibold text-[#4A0E17]">
                                        {ACTION_LABELS[r.action] || r.action}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-[#3D2817] max-w-xs">{r.summary}</td>
                                <td className="px-4 py-3 text-sm">
                                    {r.target_type === "tenant" && r.target_id ? (
                                        <Link to={`/platform/salons/${r.target_id}`} className="font-medium text-[#C9A227] hover:underline">
                                            View salon
                                        </Link>
                                    ) : (
                                        <span className="text-[#8A8178] capitalize">{r.target_type || "—"}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="px-4 py-10 text-center text-sm text-[#8A8178]">
                                    No activity recorded yet — actions will appear here as you manage the platform
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
